import { useEffect, useState } from 'react';
import { Form, FormikProvider, useFormik } from 'formik';
import { toFormikValidationSchema, toFormikValidate } from 'zod-formik-adapter';
import { Label } from '@antimatterhq/ui/components/label';
import Spacer from '@antimatterhq/ui/components/spacer';
import { Button } from '@antimatterhq/ui/components/button';
import { Text } from '@antimatterhq/ui/components/typography';
import { AxiosError } from 'axios';
import {
  useGetWorkspaceConfig,
  useOptimisticUpdateWorkspaceConfig,
} from '@/hooks/workspace/useGetWorkspaceConfig';
import {
  getWorkspaceInitialValues,
  setWorkspaceValues,
} from '@/lib/workspaceConfig/workspaceHelper';
import { WorkspaceSchema } from '@/zod/schemas/workspaceConfig/WorkspaceSchema';
import { FormError, FormInput } from '@/components/form/form';
import { useUpdateWorkspaceConfig } from '@/hooks/workspace/useUpdateWorkspaceConfig';
import { ResponseError } from '@/components/responseError/ResponseError';

export const Workspace = () => {
  const { data: workspaceConfig } = useGetWorkspaceConfig();
  const { mutateAsync: updateConfig, isPending: isUpdating } =
    useUpdateWorkspaceConfig();
  const optimisticUpdate = useOptimisticUpdateWorkspaceConfig();
  const [responseError, setResponseError] = useState<
    AxiosError<{ message: string }> | undefined
  >(undefined);

  const formik = useFormik({
    initialValues: getWorkspaceInitialValues(workspaceConfig),
    validationSchema: toFormikValidationSchema(WorkspaceSchema),
    validate: toFormikValidate(WorkspaceSchema),
    onSubmit: async (newValues) => {
      const config = setWorkspaceValues(workspaceConfig!, newValues);
      setResponseError(undefined);
      // save to API
      await updateConfig(config, {
        onSuccess: async () => {
          optimisticUpdate(config);
        },
        onError: async ({ error }) => {
          setResponseError(error as AxiosError<{ message: string }>);
        },
        toasts: {
          success: 'Workspace configuration updated',
          error: 'Failed to update workspace configuration',
        },
      });
    },
  });

  const { handleBlur, handleChange, values, setValues } = formik;

  // reset the initial values when the workspace config changes
  useEffect(() => {
    if (workspaceConfig) {
      setValues(getWorkspaceInitialValues(workspaceConfig));
    }
  }, [workspaceConfig, setValues]);

  return (
    <FormikProvider value={formik}>
      <Form>
        <Spacer size="base" />

        <Label>Default custom notebook location</Label>
        <FormInput
          name="defaultCustomNotebookLocation"
          placeholder="Enter default custom notebook location"
          onBlur={handleBlur}
          onChange={handleChange}
          value={values.defaultCustomNotebookLocation}
        />
        <FormError name="defaultCustomNotebookLocation" />

        <Spacer size="2xl" />

        <Text size="base" bold>
          Datasources
        </Text>

        <Spacer size="base" />

        <Label required>Catalog name</Label>
        <FormInput
          name="datasources.catalogName"
          placeholder="Enter catalog name"
          onBlur={handleBlur}
          onChange={handleChange}
          value={values.datasources.catalogName}
        />
        <FormError name="datasources.catalogName" />

        <Spacer size="base" />

        <Label required>Bronze schema</Label>
        <FormInput
          name="datasources.bronzeSchema"
          placeholder="Enter bronze schema"
          onBlur={handleBlur}
          onChange={handleChange}
          value={values.datasources.bronzeSchema}
        />
        <FormError name="datasources.bronzeSchema" />

        <Spacer size="base" />

        <Label required>Silver schema</Label>
        <FormInput
          name="datasources.silverSchema"
          placeholder="Enter silver schema"
          onBlur={handleBlur}
          onChange={handleChange}
          value={values.datasources.silverSchema}
        />
        <FormError name="datasources.silverSchema" />

        <Spacer size="base" />

        <Label required>Gold schema</Label>
        <FormInput
          name="datasources.goldSchema"
          placeholder="Enter gold schema"
          onBlur={handleBlur}
          onChange={handleChange}
          value={values.datasources.goldSchema}
        />
        <FormError name="datasources.goldSchema" />

        <Spacer size="base" />

        <Label>Checkpoint location</Label>
        <FormInput
          name="datasources.checkpointLocation"
          placeholder="Enter checkpoint location"
          onBlur={handleBlur}
          onChange={handleChange}
          value={values.datasources.checkpointLocation}
        />
        <FormError name="datasources.checkpointLocation" />

        <Spacer size="2xl" />

        <Text size="base" bold>
          Rules
        </Text>

        <Spacer size="base" />

        <Label>Checkpoint location</Label>
        <FormInput
          name="rules.checkpointLocation"
          placeholder="Enter checkpoint location"
          onBlur={handleBlur}
          onChange={handleChange}
          value={values.rules.checkpointLocation}
        />

        <ResponseError
          error={responseError}
          data-testid="workspace-response-error"
          justify="end"
          messageClassName="overflow-auto"
        />

        <div className="flex justify-end">
          <Button
            variant="primary"
            disabled={isUpdating}
            type="submit"
            loading={isUpdating}
          >
            Submit changes
          </Button>
        </div>
      </Form>
    </FormikProvider>
  );
};
